import { useEffect, useRef, useState } from 'react';
import { adminRequest, operationKey } from 'lib/adminSiteApi';
import { requestHeroMedia, decodeSelectedMedia } from 'lib/heroMedia';
import { useAdmin, hasCapability } from '../RequireAdmin';
import { ErrorNotice, Loading, useRemote } from './Controls';
import { Lookup } from './Records';

const candidate = value => ({ imageUrl: value?.imageUrl || '', imageAlt: value?.imageAlt || '', ...(value?.mediaId ? { mediaId: value.mediaId } : {}) });
export function MediaThumbnail({ media, label }) {
  const identity = JSON.stringify(media || null);
  const [state, setState] = useState(null);
  useEffect(() => {
    const media = JSON.parse(identity);
    if (!media?.imageUrl || (media.status && media.status !== 'ready')) return;
    const controller = new AbortController(); let result, active = true;
    const timer = setTimeout(() => controller.abort(), 20000);
    decodeSelectedMedia(media, controller.signal).then(value => {
      result = value;
      if (!controller.signal.aborted) setState({ identity, value });
      else URL.revokeObjectURL(value.displayUrl);
    }, error => { if (active) setState({ identity, error: error.name === 'AbortError' ? new Error('The image timed out. Reload or choose an available public image.') : error }); })
      .finally(() => clearTimeout(timer));
    return () => { active = false; controller.abort(); if (result) URL.revokeObjectURL(result.displayUrl); };
  }, [identity]); // The projection identity owns this image, never the previous target.
  const current = state?.identity === identity ? state : null;
  return <div className="aw-array" data-media-preview={label}>
    <strong>{label}</strong>
    {current?.value ? <img src={current.value.displayUrl} alt={media.imageAlt || ''} style={{ width: '100%', maxWidth: 360, maxHeight: 180, objectFit: 'cover' }} /> :
      <p className="aw-muted">{media?.imageUrl ? 'Loading selected image…' : 'No published image configured.'}</p>}
    <ErrorNotice error={current?.error || (media?.status === 'error' ? new Error('The published image is unavailable. Choose an available public image and publish it.') : null)} />
  </div>;
}

export default function HeroMediaEditor({ slot, inherited = false, title, description }) {
  const profile = useAdmin(), remote = useRemote(`/admin/hero-media/${slot}`, 0, { keep: true });
  const [base, setBase] = useState(null), [value, setValue] = useState(null), [live, setLive] = useState(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState(null), [receipt, setReceipt] = useState(null);
  const [verification, setVerification] = useState(''), pending = useRef(null), alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    if (remote.data && !base) {
      setBase(remote.data.record); setLive(remote.data.published);
      setValue(candidate(remote.data.record.draft || remote.data.record.published));
    }
  }, [remote.data, base]);
  async function verify(committed) {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 20000);
    try {
      const [adminResult, projection] = await Promise.all([
        adminRequest(`/admin/hero-media/${slot}`, { signal: controller.signal }), requestHeroMedia(slot, controller.signal),
      ]);
      if (committed.data.action === 'publish' && (projection.status !== 'ready'
        || projection.assetId !== committed.data.assetId || projection.publicationRevision !== committed.data.publicationRevision
        || adminResult.data.published?.assetId !== projection.assetId || adminResult.data.published?.publicationRevision !== projection.publicationRevision))
        throw new Error('The publication was committed, but its current public image could not be verified. Reload if another editor has published since.');
      if (committed.data.action === 'publish') {
        const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 20000);
        try { const checked = await decodeSelectedMedia(projection, controller.signal); URL.revokeObjectURL(checked.displayUrl); } finally { clearTimeout(timer); }
      }
      if (!alive.current) return;
      setLive(projection); setBase(adminResult.data.record);
      setValue(candidate(adminResult.data.record.draft || adminResult.data.record.published));
      setVerification('verified'); setError(null);
    } catch (err) { if (alive.current) { setVerification('pending'); setError(err.name === 'AbortError' ? new Error('Verification timed out. Retry verification to check the committed result.') : err); } }
    finally { clearTimeout(timer); }
  }
  async function save(action) {
    if (busy || !base || verification === 'pending') return;
    setBusy(true); setError(null); setReceipt(null); setVerification('');
    let committed;
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      let checked;
      try { checked = await decodeSelectedMedia(value, controller.signal); } finally { clearTimeout(timer); }
      URL.revokeObjectURL(checked.displayUrl);
      if (!alive.current) return;
      const body = { revision: base.revision, action, selection: { ...value, contentSha256: checked.contentSha256 } }, digest = JSON.stringify(body);
      if (pending.current?.digest !== digest) pending.current = { digest, key: operationKey() };
      const writeController = new AbortController(), writeTimer = setTimeout(() => writeController.abort(), 20000);
      try { committed = await adminRequest(`/admin/hero-media/${slot}`, { method: 'PUT', key: pending.current.key, body, signal: writeController.signal }); } finally { clearTimeout(writeTimer); }
      pending.current = null;
    } catch (err) { if (alive.current) { setError(err); setBusy(false); } return; }
    if (!alive.current) return;
    setReceipt(committed); setVerification('pending');
    await verify(committed);
    if (alive.current) setBusy(false);
  }
  async function reload() {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 20000);
    setBusy(true);
    try {
      const result = await adminRequest(`/admin/hero-media/${slot}`, { signal: controller.signal });
      if (!alive.current) return;
      setBase(result.data.record); setValue(candidate(result.data.record.draft || result.data.record.published));
      setLive(result.data.published); setReceipt(null); setVerification(''); setError(null);
    } catch (err) { if (alive.current) setError(err); }
    finally { clearTimeout(timer); if (alive.current) setBusy(false); }
  }
  return <section className="aw-panel" data-hero-media-editor={slot}>
    <h2>{title || (inherited ? 'Image inherited from Home' : 'Page media')}</h2>
    {description && <p className="aw-muted">{description}</p>}
    <Loading state={remote}>
      {base && <>
        <p className="aw-muted">{remote.data?.label}. {remote.data?.roles}. Revision {base.revision}.</p>
        {inherited && <p>Contact redirects to Home. Privacy and Terms also use the Home image. Edit the Home page media to change it.</p>}
        <MediaThumbnail media={live} label="Currently live" />
        {value && JSON.stringify(candidate(live)) !== JSON.stringify(candidate(value)) && <MediaThumbnail media={value} label="Editing / draft" />}
        {!inherited && hasCapability(profile, 'content.edit') && <fieldset disabled={busy || verification === 'pending'}>
          <label>Image URL<input value={value?.imageUrl || ''} onChange={e => setValue(v => ({ ...v, imageUrl: e.target.value, mediaId: '' }))} /></label>
          <label>Image alt<input value={value?.imageAlt || ''} onChange={e => setValue(v => ({ ...v, imageAlt: e.target.value }))} /></label>
          <Lookup domain="media" label="Browse media library" onSelect={row => {
            if (row.status !== 'published' || !row.public || !row.contentType?.startsWith('image/')) { setError(new Error('Choose a published public image.')); return; }
            setValue({ imageUrl: row.url, imageAlt: row.altText || '', mediaId: row.id }); setError(null);
          }} />
          <div className="aw-row">
            <button type="button" className="aw-button" onClick={() => save('draft')}>Save image draft</button>
            {hasCapability(profile, 'content.publish') && <button type="button" className="aw-button aw-primary" onClick={() => save('publish')}>Publish image</button>}
          </div>
        </fieldset>}
        <ErrorNotice error={error} />
        {receipt && <div className="aw-notice" role="status">
          {verification === 'pending' ? 'Write committed; verification pending.' : receipt.data.action === 'publish' ? 'Published image verified.' : 'Image draft saved. The live image is unchanged.'}
          <small>Receipt: {receipt.requestId}. Revision {receipt.data.revision}.</small>
        </div>}
        {verification === 'pending' ? <button type="button" className="aw-button" disabled={busy} onClick={() => verify(receipt)}>Retry verification</button> :
          <button type="button" className="aw-button" disabled={busy} onClick={reload}>Reload image and discard local selection</button>}
      </>}
    </Loading>
  </section>;
}
